import {Injectable, OnDestroy} from '@angular/core';
import {GlobalService} from '../global/global.service';
import {Subject, Subscription} from 'rxjs';
import {UserWsInfo} from './model/user-ws-info';
import {AuthStorage} from '../auth/auth-storage';
import {debounceTime} from 'rxjs/operators';
import {ReconnectingSocket} from './reconnecting-socket';

const ALARM_COUNT_TOPIC = `/users/alarm/alarm-count`;
const ALARM_WS_ENDPOINT = `/app/alarms-ws`;

const RECONNECT_TIMEOUT = 60000;
const THROTTLE_TIME = 1000;

enum AlarmWsConnectionType {
  ALARM = 'ALARM',
  NOTICE = 'NOTICE',
  PERSONAL = 'PERSONAL'
}

@Injectable({
  providedIn: 'root'
})
export class AlarmsWebSocketService implements OnDestroy {

  private socket: ReconnectingSocket;

  private debouncedUpdateAlarm = new Subject<void>();
  private debouncedUpdateNotice = new Subject<void>();
  private debouncedUpdatePersonalNotification = new Subject<void>();

  private debouncedUpdateAlarmSubs: Subscription;
  private debouncedUpdateNoticeSubs: Subscription;
  private debouncedUpdatePersonalNotificationSubs: Subscription;

  private alarmCountSource = new Subject<number>();
  public alarmCount$ = this.alarmCountSource.asObservable();

  private noticeCountSource = new Subject<number>();
  public noticeCount$ = this.noticeCountSource.asObservable();

  private personalNotificationCountSource = new Subject<number>();
  public personalNotificationCount$ = this.personalNotificationCountSource.asObservable();

  constructor(private globalService: GlobalService) {

    this.socket = new ReconnectingSocket({
      hiddenReconnectDelay: RECONNECT_TIMEOUT,
      canConnect: () => AuthStorage.isLoggedClientEmployee(),
      fetchUserInfo: () => this.globalService.userAlarmWsInfo(),
      headers: (userInfo: UserWsInfo) => ({
        employeeId: userInfo.employeeId,
        clientId: userInfo.clientId,
        token: userInfo.token,
        accountId: userInfo.accountId
      }),
      onConnected: () => this.socket.subscribe(ALARM_COUNT_TOPIC, (event) =>
        this.handleAlarmCount(JSON.parse(event.body))
      ),
      onResume: () => this.sendUpdateAlarmSubs()
    });

    this.debouncedUpdateAlarmSubs = this.debouncedUpdateAlarm
      .pipe(debounceTime(THROTTLE_TIME))
      .subscribe(() => this.sendUpdateAlarmSubs());

    this.debouncedUpdateNoticeSubs = this.debouncedUpdateNotice
      .pipe(debounceTime(THROTTLE_TIME))
      .subscribe(() => this.sendUpdateNoticeSubs());

    this.debouncedUpdatePersonalNotificationSubs = this.debouncedUpdatePersonalNotification
      .pipe(debounceTime(THROTTLE_TIME))
      .subscribe(() => this.sendUpdatePersonalNotificationSubs());
  }

  public getUserAndConnect(): void {
    this.socket.connect();
  }

  public updateAlarmSubs(): void {
    this.debouncedUpdateAlarm.next();
  }

  public sendUpdateAlarmSubs(): void {
    this.socket.send(ALARM_WS_ENDPOINT, AlarmWsConnectionType.ALARM);
  }

  public updateNoticeSubs(): void {
    this.debouncedUpdateNotice.next();
  }

  public sendUpdateNoticeSubs(): void {
    this.socket.send(ALARM_WS_ENDPOINT, AlarmWsConnectionType.NOTICE);
  }

  public updatePersonalNotificationSubs(): void {
    this.debouncedUpdatePersonalNotification.next();
  }

  public sendUpdatePersonalNotificationSubs(): void {
    this.socket.send(ALARM_WS_ENDPOINT, AlarmWsConnectionType.PERSONAL);
  }

  public disconnect(): void {
    this.socket.disconnect();
  }

  public ngOnDestroy(): void {
    this.debouncedUpdateAlarmSubs.unsubscribe();
    this.debouncedUpdateNoticeSubs.unsubscribe();
    this.debouncedUpdatePersonalNotificationSubs.unsubscribe();
    this.socket.destroy();
  }

  private handleAlarmCount = (alarmWSEntry: any) => {
    alarmWSEntry = alarmWSEntry != null
      ? alarmWSEntry
      : {alarmCount: 0, noticeCount: 0, personalCount: 0}
    this.alarmCountSource.next(
      alarmWSEntry.alarmCount
    );
    this.noticeCountSource.next(
      alarmWSEntry.noticeCount
    )
    this.personalNotificationCountSource.next(
      alarmWSEntry.personalCount
    )
  }

}
